import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";


const ago = (iso) => {
    if (!iso) return "never";
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

export default function CronPanel({ appId, app, socketJobs }) {
    const [jobs, setJobs] = useState([]);
    const [expected, setExpected] = useState(app?.config?.expected_s || 3600);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/cron-jobs`, { credentials: "include" });
                if (r.ok) setJobs((await r.json()).jobs || []);
            } catch { /* stream will fill it */ }
        })();
    }, [appId]);

    useEffect(() => {
        if (socketJobs) setJobs(socketJobs);
    }, [socketJobs]);

    const save = async () => {
        setSaving(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/config`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ config: { expected_s: Number(expected) || 3600 } }),
            });
            if (!r.ok) throw new Error();
            toast.success("Expected interval saved");
        } catch {
            toast.error("Could not save interval");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="cron-panel" className="glass-panel rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="flex items-center gap-2 font-display text-sm font-bold tracking-tight text-slate-100">
                    <CalendarClock className="h-4 w-4 text-teal-400" />
                    Cron jobs
                </h3>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                        expect every
                    </span>
                    <input
                        data-testid="cron-interval-input"
                        type="number"
                        min="60"
                        value={expected}
                        onChange={(e) => setExpected(e.target.value)}
                        className="w-24 rounded-md border border-slate-800 bg-slate-950/70 px-2.5 py-1.5 font-mono text-[11px] text-slate-200 focus:border-teal-500/60 focus:outline-none"
                    />
                    <span className="font-mono text-[10px] text-slate-500">sec (min 60)</span>
                    <button
                        data-testid="cron-save-button"
                        onClick={save}
                        disabled={saving}
                        className="rounded-md bg-teal-500 px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-950 transition-colors hover:bg-teal-400 disabled:opacity-50"
                    >
                        {saving ? "saving…" : "save"}
                    </button>
                </div>
            </div>

            <div className="mt-4 space-y-2">
                {jobs.length === 0 && (
                    <p
                        data-testid="cron-empty"
                        className="rounded-md border border-dashed border-slate-800 px-3 py-4 text-center font-mono text-[10.5px] leading-relaxed text-slate-600"
                    >
                        No heartbeats yet — add one line to your job:
                        <span className="mt-1 block text-teal-300/80">
                            {'curl -X POST …/api/agent/heartbeat -H "X-Agent-Key: KEY" -d \'{"job":"nightly-backup"}\''}
                        </span>
                    </p>
                )}
                {jobs.map((j) => (
                    <div
                        key={j.job}
                        data-testid={`cron-job-${j.job}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-slate-800/70 bg-slate-900/50 px-3.5 py-2.5"
                    >
                        <div className="min-w-0">
                            <p className="truncate font-mono text-xs font-semibold text-slate-200">{j.job}</p>
                            <p className="font-mono text-[9.5px] text-slate-600">
                                {j.host || "unknown host"} · last beat {ago(j.last_seen)}
                            </p>
                        </div>
                        <span
                            className={`shrink-0 rounded border px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-widest ${
                                j.missed
                                    ? "border-rose-500/30 bg-rose-950/40 text-rose-300"
                                    : "border-emerald-500/30 bg-emerald-950/40 text-emerald-300"
                            }`}
                        >
                            {j.missed ? "missed" : "on schedule"}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
